import { useEffect, useRef } from 'react';
import cx from 'classnames';

import { useScrollToTopOnLocationChange } from 'src/lib/hooks';
import { ROUTES } from 'src/routes';
import { useCurrentCustomerId, useProjectActions } from 'src/store';
import { NavigationSidebarPanel } from 'src/views/NavigationSidebarPanel';
import { GeneralNotifications } from 'src/views/NotificationsCenter';
import { PerformancePredictionsBanner } from 'src/views/PerformancePredictionsBanner';
import { WebinarBanner } from 'src/views/WebinarBanner';

import { BANNER_TYPE } from '../../lib';

import { useStyles } from './useStyles';

export const PrivateRouteWrapper = ({
  classes,
  children,
  displayNavigation,
  displayNotifications,
  onlyImportantNotifications,
  floatingNotifications,
  hideOverflow,
  showWebinarBanner,
  showPerformancePredictionsBanner,
}) => {
  const styles = useStyles({ classes });
  const scrollContainerRef = useRef();
  const currentCustomerId = useCurrentCustomerId();
  const projectActions = useProjectActions();

  useScrollToTopOnLocationChange(scrollContainerRef);

  // When we select another customer - clear state of current one
  useEffect(() => {
    const oldCustomerId = currentCustomerId;
    return () => {
      if (oldCustomerId) {
        projectActions.clearStore();
      }
    };
  }, [currentCustomerId, projectActions]);

  const bannerType = getBannerType({
    showPerformancePredictionsBanner,
    displayNavigation,
    showWebinarBanner,
  });

  return (
    <div
      className={styles.root}
      data-banner-type={bannerType}
    >
      {bannerType === BANNER_TYPE.performancePredictions && (
        <PerformancePredictionsBanner className={styles.banner} />
      )}

      {bannerType === BANNER_TYPE.webinar && <WebinarBanner className={styles.banner} />}

      {displayNavigation && <NavigationSidebarPanel className={styles.navigationSidebarPanel} />}

      <div
        className={cx(styles.content, {
          [styles.overflowHidden]: hideOverflow,
        })}
        ref={scrollContainerRef}
      >
        {displayNotifications && (
          <GeneralNotifications
            onlyImportant={onlyImportantNotifications}
            floating={floatingNotifications}
          />
        )}
        {children}
      </div>
    </div>
  );
};

const getBannerType = ({
  showPerformancePredictionsBanner,
  displayNavigation,
  showWebinarBanner,
}) => {
  // Show performance predictions banner on all pages except "upgrade" and "checkout"
  if (showPerformancePredictionsBanner) {
    const pathname = window.location.pathname;
    if (![ROUTES.UPGRADE, ROUTES.CHECKOUT].includes(pathname)) {
      return BANNER_TYPE.performancePredictions;
    }
  }

  if (displayNavigation && showWebinarBanner) {
    return BANNER_TYPE.webinar;
  }

  return null;
};
